import CustomPagination from "../../__utilities/components/CustomPagination";
import { formatNumber } from "__utilities/functions/public";
import { useAdmin } from "_data/hooks/admin/useAdmin";
import { useEffect, useState } from "react";
import {
  DataGrid,
  GridFooterContainer,
  gridVisibleColumnDefinitionsSelector,
  useGridApiContext,
  useGridSelector,
  gridFilteredSortedRowIdsSelector,
  gridRowsLookupSelector,
  useGridApiRef,
} from "@mui/x-data-grid";
import { Box, Tooltip } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

export default function ActivityLog() {
  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(100);
  const { getListActivitiesData } = useAdmin();
  const apiRef = useGridApiRef();

  const getActivities = async () => {
    await getListActivitiesData((success, activities) => {
      if (success) {
        let activitiesData = activities
          .map((u) => ({ ...u, id: uuidv4() }))
          .filter((u) => !u.isDemo);

        setActivities(activitiesData);
      }
    });
  };

  useEffect(() => {
    getActivities();
  }, []);

  const columns = [
    { field: "extractId", headerName: "Extract Id", flex: 1 },
    {
      field: "timestampCreated",
      headerName: "Date",
      renderCell: (params) => {
        return (
          <span>
            {params.value
              ? new Date(params.value).toLocaleString("en-GB", {
                  day: "numeric",
                  month: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hourCycle: "h23",
                })
              : ""}{" "}
            -{" "}
            {params.row
              ? new Date(params.row.timestampCompleted).toLocaleTimeString(
                  "en-GB",
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hourCycle: "h23",
                  },
                )
              : ""}
          </span>
        );
      },
      flex: 1.2,
    },
    { field: "username", headerName: "Username", flex: 1.6 },
    { field: "displayName", headerName: "Display name", flex: 1.6 },
    { field: "extractType", headerName: "Court type", flex: 0.6 },
    {
      field: "isEdit",
      headerName: "Type",
      renderCell: (params) => {
        return <span>{params.value ? "Edit" : "New"}</span>;
      },
      flex: 0.4,
    },
    {
      field: "numberOfDocs",
      headerName: "No. docs",
      headerAlign: "right",
      align: "right",
      flex: 0.5,
    },
    {
      field: "requestedPages",
      headerName: "Req. pages",
      headerAlign: "right",
      align: "right",
      flex: 0.5,
    },
    {
      field: "totalPages",
      headerName: "Total pages",
      headerAlign: "right",
      align: "right",
      flex: 0.5,
    },
    {
      field: "fileSizeText",
      headerName: "File size (MB)",
      headerAlign: "right",
      align: "right",
      flex: 0.55,
      sortComparator: (v1, v2) => {
        const toNum = (v) => {
          if (v === undefined || v === null) return NaN;
          const cleaned = String(v).replace(/[^0-9.-]+/g, "");
          const n = parseFloat(cleaned);
          return Number.isFinite(n) ? n : NaN;
        };

        const n1 = toNum(v1);
        const n2 = toNum(v2);

        if (Number.isNaN(n1) && Number.isNaN(n2)) return 0;
        if (Number.isNaN(n1)) return -1;
        if (Number.isNaN(n2)) return 1;
        if (n1 < n2) return -1;
        if (n1 > n2) return 1;
        return 0;
      },
    },
    {
      field: "status",
      headerName: "Status/Result",
      headerAlign: "right",
      align: "right",
      flex: 0.6,
    },
  ];

  const pageCount = Math.ceil(activities.length / pageSize);

  const handlePageChange = (newPage) => {
    if (newPage < 0 || newPage >= pageCount) return;
    setPage(newPage);
    apiRef.current.setPage(newPage);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0">
        <DataGrid
          apiRef={apiRef}
          className="data-grid-activities text-[9px] tall:text-sm text-primaryBlue"
          columnHeaderHeight={35}
          rowHeight={30}
          slots={{
            footer: () => <CustomFooter />,
          }}
          slotProps={{
            columnMenu: {
              sx: {
                minWidth: "150px",
                "& .MuiDivider-root": {
                  marginBlock: "0px",
                },
                "& .MuiButtonBase-root": {
                  paddingInline: "5px",
                },
                "& .MuiSvgIcon-root": {
                  scale: 0.6,
                },
                "& .MuiListItemText-primary": {
                  fontSize: {
                    sm: "10px",
                    md: "10px",
                    lg: "10px",
                    xl: "13px",
                  },
                },
              },
            },
          }}
          pageSizeOptions={[100]}
          hideFooterSelectedRowCount
          pagination
          rows={activities}
          columns={columns}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 1 ? "bg-[#F6F5F3]" : ""
          }
          getRowId={(row) => row.id}
          sx={{
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#F0F3F5",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: "#003057",
            },
            "& .MuiDataGrid-cell": {
              color: "#003057",
            },
            "& .MuiDataGrid-scrollbarFiller.MuiDataGrid-scrollbarFiller--header":
              {
                backgroundColor: "#F0F3F5",
              },
          }}
        />
      </div>

      <Box className="flex-shrink-0 mt-2">
        <CustomPagination
          page={page}
          pageCount={pageCount}
          onPageChange={handlePageChange}
        />
      </Box>
    </div>
  );
}

function CustomFooter() {
  const apiRef = useGridApiContext();
  const visibleColumns = useGridSelector(
    apiRef,
    gridVisibleColumnDefinitionsSelector,
  );

  const filteredSortedRowIds = useGridSelector(
    apiRef,
    gridFilteredSortedRowIdsSelector,
  );
  const rowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);

  const filteredRows = filteredSortedRowIds.map((id) => rowsLookup[id]);

  let totalCity = filteredRows.filter(
    (activity) => activity.extractType === "City",
  ).length;
  let totalNational = filteredRows.filter(
    (activity) => activity.extractType === "National",
  ).length;
  let totalSupreme = filteredRows.filter(
    (activity) => activity.extractType === "Supreme",
  ).length;

  let totalNoDocs = filteredRows.reduce(
    (sum, activity) => sum + activity.numberOfDocs,
    0,
  );
  let totalRequestedPages = filteredRows.reduce(
    (sum, activity) => sum + activity.requestedPages,
    0,
  );
  let totalPages = filteredRows.reduce(
    (sum, activity) => sum + activity.totalPages,
    0,
  );

  return (
    <GridFooterContainer
      className={`flex flex-col h800:!min-h-[30px] h801:!min-h-[40px]`}
      sx={{
        borderTop: "1px solid #e0e0e0",
        position: "relative",
      }}
    >
      <Box sx={{ display: "flex", width: "100%", backgroundColor: "#F0F3F5" }}>
        {visibleColumns.map((col) => {
          let content;

          if (col.field === "extractId")
            content = `Total activities: ${formatNumber(filteredRows.length)}`;
          else if (col.field === "extractType") {
            content = (
              <Box sx={{ display: "flex", flexDirection: "row", gap: "4px" }}>
                <Tooltip title={`City`}>
                  <span className="cursor-default">
                    {formatNumber(totalCity)}
                  </span>
                </Tooltip>
                <span>/</span>
                <Tooltip title={`National`}>
                  <span className="cursor-default">
                    {formatNumber(totalNational)}
                  </span>
                </Tooltip>
                <span>/</span>
                <Tooltip title={`Supreme`}>
                  <span className="cursor-default">
                    {formatNumber(totalSupreme)}
                  </span>
                </Tooltip>
              </Box>
            );
          } else if (col.field === "numberOfDocs")
            content = formatNumber(totalNoDocs);
          else if (col.field === "requestedPages")
            content = formatNumber(totalRequestedPages);
          else if (col.field === "totalPages")
            content = formatNumber(totalPages);

          const isRightAligned = [
            "numberOfDocs",
            "requestedPages",
            "totalPages",
          ].includes(col.field);

          return (
            <Box
              key={col.field}
              sx={{
                width: col.computedWidth,
                minWidth: col.minWidth,
                maxWidth: col.maxWidth,
                display: "flex",
                alignItems: "center",
                justifyContent: isRightAligned ? "flex-end" : "flex-start",
                boxSizing: "border-box",
                fontWeight: "bold",
                color: "#003057",
                p: 1.2,
                overflow: "hidden",
              }}
            >
              {content}
            </Box>
          );
        })}
      </Box>
    </GridFooterContainer>
  );
}
